import { UNICODE } from '@shared/models/unicode.model';
import { guards } from '../typeguards.helpers';
import { getScientificDisplayNumber } from './get-scientific-display-number.helper';

/**
 * Formats a part of a scientific number into a string
 * @param part - The part to format
 * @returns The formatted part
 */
const formatPart = (part: Intl.NumberFormatPart): string => {
  switch (part.type) {
    case 'decimal':
      return '.';
    case 'literal':
      return '';
    case 'compact':
      return part.value.toUpperCase();
    case 'minusSign':
      return UNICODE.MATH.MINUS;
    default:
      return part.value;
  }
};

const SCIENTIFIC_FORMAT_THRESHOLD = 1_000_000_000_000_000; // 1 quadrillion

/**
 * Formats a number into compact notation like: "1.34M"
 * will format to scientific notation if the number is equal to or greater than {@link SCIENTIFIC_FORMAT_THRESHOLD}
 * note: this function is not i18n aware. It displays a nodon-specific format.
 * @param value - The number to format
 * @param decimals - Max fraction digits for compact output. When omitted, uses a
 *   significant-digit cap (three digits when the scaled mantissa is 100–999 so e.g. 203 → "203").
 *   **Including `0` is valid** — forces {@link Intl.NumberFormat} fraction digits instead of that cap.
 * @returns The formatted number
 */
export const getCompactDisplayNumber = (
  value: number,
  decimals?: number,
): string => {
  try {
    if (value === 0) return '0';
    if (!guards.is.finiteNumber(value)) return String(value);
    if (Math.abs(value) >= SCIENTIFIC_FORMAT_THRESHOLD)
      return getScientificDisplayNumber(value, {
        decimals: decimals ?? 1,
        notation: 'compact',
      });

    const decimalConf = guards.is.number(decimals)
      ? { maximumFractionDigits: decimals }
      : { maximumSignificantDigits: getMaximumSignificantDigits(value) };
    const formattedParts = new Intl.NumberFormat('en-US', {
      notation: 'compact',
      ...decimalConf,
    }).formatToParts(value);

    return formattedParts.map(formatPart).join('');
  } catch {
    console.error('Error formatting scientific number', value, decimals);
    return String(value);
  }
};

/**
 * Significant-digit budget for default compact formatting: scale like K/M/B/T
 * mantissas, then 1 if the scaled value is below 1, else 3 in [100, 1000), else 2.
 *
 * @param value - Full numeric value (sign ignored for the digit rule).
 * @returns `maximumSignificantDigits` passed to {@link Intl.NumberFormat} compact mode.
 */
export const getMaximumSignificantDigits = (value: number) => {
  // Scale down like compact groups (K/M/B/…) so we pick digits for the mantissa shown before the suffix.
  let v = Math.abs(value);
  while (v >= 1000) v = v / 1000;
  if (v < 1) return 1;
  // 2 sig figs here used to turn 203 → "200"; 100–999 needs three digits for a sensible integer display.
  if (v >= 100 && v < 1000) return 3;
  return 2;
};
